import { isNative } from "@/lib/capacitor";

// Haptic feedback for the touch interactions that deserve one: sending,
// long-pressing, switching tabs, recording a voice note.
//
// Two backends behind one API, like notifications.ts: @capacitor/haptics in the
// native shells (Taptic Engine on iOS, the vibrator on Android) and
// `navigator.vibrate` on the web — which Android browsers honour and iOS Safari
// ignores entirely, so the web fallback is best-effort by nature.
//
// Every call is fire-and-forget and swallows its errors: a missing vibrator
// must never break the interaction it decorates.

const ENABLED_KEY = "mandoob:haptics";

type HapticsModule = typeof import("@capacitor/haptics");

let nativeModule: Promise<HapticsModule | null> | null = null;

function loadNative(): Promise<HapticsModule | null> {
  if (!isNative()) return Promise.resolve(null);
  // Dynamic so the plain-web bundle never pulls the plugin in.
  nativeModule ??= import("@capacitor/haptics").catch(() => null);
  return nativeModule;
}

// Warm the plugin on the shells: a tap should buzz now, not after a chunk
// finishes loading.
void loadNative();

function webVibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // No vibrator, or the browser blocks it outside a user gesture.
  }
}

// --- User preference -------------------------------------------------------
// Some drivers hate buzzing phones; the switch lives in Settings next to
// notifications and defaults to on.

export function hapticsEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setHapticsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, enabled ? "on" : "off");
  } catch {
    // storage unavailable — preference is per-session only
  }
}

/** False on hardware that cannot vibrate at all, so the switch can be hidden. */
export function hapticsSupported(): boolean {
  return isNative() || typeof navigator?.vibrate === "function";
}

// --- Feedback --------------------------------------------------------------

export type ImpactWeight = "light" | "medium" | "heavy";

const WEB_IMPACT_MS: Record<ImpactWeight, number> = {
  light: 10,
  medium: 20,
  heavy: 35,
};

/** A physical "that landed" tap — sending, capturing, confirming. */
export function hapticImpact(weight: ImpactWeight = "light"): void {
  if (!hapticsEnabled()) return;
  void loadNative().then((haptics) => {
    if (!haptics) {
      webVibrate(WEB_IMPACT_MS[weight]);
      return;
    }
    const { Haptics, ImpactStyle } = haptics;
    const style =
      weight === "heavy"
        ? ImpactStyle.Heavy
        : weight === "medium"
          ? ImpactStyle.Medium
          : ImpactStyle.Light;
    void Haptics.impact({ style }).catch(() => undefined);
  });
}

/** The lighter tick for picking something: tabs, long-press menus, toggles. */
export function hapticSelection(): void {
  if (!hapticsEnabled()) return;
  void loadNative().then(async (haptics) => {
    if (!haptics) {
      webVibrate(5);
      return;
    }
    const { Haptics } = haptics;
    try {
      const { Capacitor } = await import("@capacitor/core");
      if (Capacitor.getPlatform() === "android") {
        // Android maps the selection generator to a surprisingly strong
        // buzz — a tiny raw vibration reads as the intended soft tick.
        await Haptics.vibrate({ duration: 3 });
        return;
      }
      // iOS wants the Taptic generator primed and released around the tick.
      await Haptics.selectionStart();
      await Haptics.selectionChanged();
      await Haptics.selectionEnd();
    } catch {
      // Generator unavailable.
    }
  });
}

/** The outcome of something the driver waited for. */
export function hapticNotification(
  type: "success" | "warning" | "error",
): void {
  if (!hapticsEnabled()) return;
  void loadNative().then((haptics) => {
    if (!haptics) {
      webVibrate(
        type === "success"
          ? 15
          : type === "warning"
            ? [20, 60, 20]
            : [30, 60, 30],
      );
      return;
    }
    const { Haptics, NotificationType } = haptics;
    const notificationType =
      type === "success"
        ? NotificationType.Success
        : type === "warning"
          ? NotificationType.Warning
          : NotificationType.Error;
    void Haptics.notification({ type: notificationType }).catch(
      () => undefined,
    );
  });
}
